import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, Loading, Toast, useAsync } from '../components/common';
import type { ParentStatus, Principal } from '../types';

const STATUS_TONE: Record<ParentStatus, 'ok' | 'warn' | 'default'> = {
  active: 'ok',
  draft: 'warn',
  suspended: 'default',
};

export default function ParentsPage({ principal }: { principal: Principal }) {
  const { data, error, loading, reload } = useAsync(() => api.listParents(), []);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'info' | 'error' } | null>(null);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>DSP parents</h1>
          <p>
            One record per parent company. Each owns its own buckets, folder names and filename
            templates outright — presets only copy values in at creation time.
          </p>
        </div>
        {principal.role === 'admin' && (
          <button className="primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Cancel' : 'New parent'}
          </button>
        )}
      </div>

      {creating && (
        <CreateParent
          onCreated={(key) => {
            setCreating(false);
            setToast({ message: `Created ${key} as draft.`, tone: 'info' });
            reload();
          }}
          onError={(message) => setToast({ message, tone: 'error' })}
        />
      )}

      {loading || !data ? (
        <Loading error={error} />
      ) : (
        <div className="card">
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Parent</th>
                  <th>Parent_Company value</th>
                  <th>Token</th>
                  <th>Prod bucket</th>
                  <th>QA bucket</th>
                  <th>Brands</th>
                  <th>Files</th>
                  <th>Snowflake</th>
                  <th>Required bindings</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.map((p) => (
                  <tr key={p.parent_key}>
                    <td>
                      <Link to={`/parents/${p.parent_key}`}>{p.display_name}</Link>
                      <div className="small muted mono">{p.parent_key}</div>
                    </td>
                    <td className="mono">{p.dsp_parent_company_value}</td>
                    <td className="mono">{p.filename_token}</td>
                    <td className="mono small">{p.bucket_prod}</td>
                    <td className="mono small">
                      {p.bucket_qa ?? <span className="muted">none</span>}
                    </td>
                    <td>{p.brand_count}</td>
                    <td>{p.file_type_count}/10</td>
                    <td className="mono small">
                      {p.snowflake_profile_key ?? <span className="muted">unset</span>}
                    </td>
                    <td>
                      {p.required_bindings_complete ? (
                        <Badge tone="ok">complete</Badge>
                      ) : (
                        <Badge tone="danger">
                          {p.missing_required_bindings.length} missing
                        </Badge>
                      )}
                    </td>
                    <td>
                      <Badge tone={STATUS_TONE[p.status]}>{p.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {toast && <Toast {...toast} onDone={() => setToast(null)} />}
    </>
  );
}

function CreateParent({
  onCreated,
  onError,
}: {
  onCreated: (key: string) => void;
  onError: (message: string) => void;
}) {
  const [form, setForm] = useState({
    parent_key: '',
    display_name: '',
    dsp_parent_company_value: '',
    filename_token: '',
    prod: '',
    qa: '',
    website: '',
    preset: 'clarity_template',
  });
  const [busy, setBusy] = useState(false);

  function set(field: keyof typeof form, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.createParent({
        parent_key: form.parent_key,
        display_name: form.display_name,
        dsp_parent_company_value: form.dsp_parent_company_value,
        filename_token: form.filename_token,
        website: form.website || null,
        preset: form.preset,
        buckets: { prod: form.prod, qa: form.qa || null, eventbridge_enabled: false },
      });
      onCreated(form.parent_key);
    } catch (err) {
      onError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2>
        New parent
        <span className="hint">created as draft; activate only after validation passes</span>
      </h2>

      <div className="row">
        <label className="field">
          <span>parent_key (lowercase slug, permanent)</span>
          <input
            value={form.parent_key}
            onChange={(e) => set('parent_key', e.target.value)}
            placeholder="accs"
            pattern="[a-z][a-z0-9_]{1,31}"
            required
          />
        </label>
        <label className="field">
          <span>Display name</span>
          <input
            value={form.display_name}
            onChange={(e) => set('display_name', e.target.value)}
            placeholder="ACCS"
            required
          />
        </label>
        <label className="field">
          <span>Zoho DSP_Parent_Company value</span>
          <input
            value={form.dsp_parent_company_value}
            onChange={(e) => set('dsp_parent_company_value', e.target.value)}
            placeholder="ACCS"
            required
          />
        </label>
      </div>

      <div className="row">
        <label className="field">
          <span>filename_token (hyphenated)</span>
          <input
            value={form.filename_token}
            onChange={(e) => set('filename_token', e.target.value)}
            placeholder="accs"
            pattern="[a-z0-9]+(-[a-z0-9]+)*"
            required
          />
        </label>
        <label className="field">
          <span>Production bucket</span>
          <input
            value={form.prod}
            onChange={(e) => set('prod', e.target.value)}
            placeholder="accs-skybridge-datashare"
            required
          />
        </label>
        <label className="field">
          <span>QA bucket (optional)</span>
          <input value={form.qa} onChange={(e) => set('qa', e.target.value)} />
        </label>
      </div>

      <div className="row">
        <label className="field">
          <span>Website</span>
          <input value={form.website} onChange={(e) => set('website', e.target.value)} />
        </label>
        <label className="field">
          <span>Folder preset (copied in once, then owned by this parent)</span>
          <select value={form.preset} onChange={(e) => set('preset', e.target.value)}>
            <option value="clarity_template">Clarity-style folder names</option>
            <option value="debt_blue_template">Debt Blue-style folder names</option>
            <option value="none">Empty — configure each file type by hand</option>
          </select>
        </label>
      </div>

      <div className="actions">
        <button className="primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create parent'}
        </button>
      </div>
    </form>
  );
}
